/**
 * ButtonGroup Component
 * A group of buttons for selecting a single option (like radio buttons but styled as buttons)
 * Ideal for 2-4 options where a dropdown would be overkill
 */

import React from 'react';
import clsx from 'clsx';

export interface ButtonGroupOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface ButtonGroupProps {
  options: ButtonGroupOption[];
  value: string;
  onChange: (value: string) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  options,
  value,
  onChange,
  size = 'md',
  disabled = false,
  className,
}) => {
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  return (
    <div className={clsx('inline-flex rounded-lg border border-gray-300 bg-white', className)}>
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => !disabled && onChange(option.value)}
            disabled={disabled}
            className={clsx(
              'inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:z-10',
              sizeStyles[size],
              // Border radius
              isFirst && 'rounded-l-lg',
              isLast && 'rounded-r-lg',
              // Border between buttons
              !isLast && 'border-r border-gray-300',
              // Selected state
              isSelected
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-white text-gray-700 hover:bg-gray-50',
              // Disabled state
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {option.icon && <span className="mr-1.5">{option.icon}</span>}
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default ButtonGroup;
